import React, { FC, useMemo, useState } from 'react';
import { Stack, StackProps } from '@mui/material';
import { DateTime } from 'luxon';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { completeDateFormat } from 'utilities/formatDate';
import { colors } from 'styles/colors';
import RecentHiringActivityCard, {
  IRecentHiringActivityCardData,
} from './recentHiringActivityCard';
import RecentHiringActivityChip from './recentHiringActivityChip';

export interface IRecentHiringActivityGroupedListProps extends StackProps {
  id: string;
  relativeDaysLimit?: number;
  recentDaysLimit?: number;
  enableDateTextSuffix?: boolean;
  data: IRecentHiringActivityCardData[];
  i18nKeyForEmptyState?: string;
  endOfListI18Key?: string;
}

const RecentHiringActivityGroupedList: FC<
  IRecentHiringActivityGroupedListProps
> = ({
  data,
  id,
  recentDaysLimit = 7,
  relativeDaysLimit = 2,
  enableDateTextSuffix = true,
  i18nKeyForEmptyState,
  endOfListI18Key,
  ...props
}) => {
  const [sortedData, setSortedData] = useState<IRecentHiringActivityCardData[]>(
    []
  );

  const dates = useMemo(() => {
    const _sortedData = data?.sort((a, b) => {
      return (
        DateTime.fromISO(b.date).toMillis() -
        DateTime.fromISO(a.date).toMillis()
      );
    });
    setSortedData(_sortedData);
    if (recentDaysLimit) {
      return Array.from({ length: recentDaysLimit }, (_, i) =>
        DateTime.now().minus({ days: i })
      );
    } else {
      const uniqueDate = [
        ...new Set(data?.map((tag) => DateTime.fromISO(tag?.date).toISODate())),
      ];
      const sortDataByDate = uniqueDate?.sort((a, b) => {
        return DateTime.fromISO(b).toMillis() - DateTime.fromISO(a).toMillis();
      });
      return sortDataByDate.map((date) => DateTime.fromISO(date));
    }
  }, [recentDaysLimit, data]);

  const getDateTextSuffixWithLimit = (
    date: DateTime,
    index: number,
    formattedDate: string
  ) => {
    return (
      index < relativeDaysLimit
        ? date.toRelativeCalendar()?.toLowerCase()
        : formattedDate
    )!;
  };

  const getDateTextSuffix = (date: DateTime, formattedDate: string) => {
    const dateObj = date?.diffNow('days');
    /* istanbul ignore else */
    if (Math.abs(Math.round(dateObj.days)) < relativeDaysLimit) {
      return date?.toRelativeCalendar()?.toLowerCase() ?? '';
    }
    return formattedDate;
  };

  return (
    <Stack
      id={id}
      data-testid={id}
      gap='0.75rem'
      role='list'
      {...props}
      sx={{ ...props.sx, justifyContent: data?.length ? '' : 'center' }}
    >
      {!!sortedData?.length ? (
        <>
          {dates.map((date: DateTime, index) => {
            const formattedDate = completeDateFormat(date);
            const activities = sortedData.filter(
              (item) => completeDateFormat(item.date) === formattedDate
            );
            const noActivity = activities.length === 0;
            const dateTextSuffix = !noActivity
              ? ` (${activities.length})`
              : undefined;

            const groupId = `${id}-grouped-list-${index}`;
            return (
              <Stack
                key={index}
                id={groupId}
                data-testid={groupId}
                role='list'
                sx={{
                  flexDirection: noActivity ? 'row' : 'column',
                  justifyContent: noActivity ? 'space-between' : 'normal',
                  alignItems: noActivity ? 'center' : 'flex-start',
                  backgroundColor: noActivity
                    ? colors.background.lightGray
                    : 'transparent',
                  borderRadius: ({ typography }) => typography.pxToRem(8),
                  p: ({ typography }) => typography.pxToRem(noActivity ? 8 : 0),
                  gap: ({ typography }) => typography.pxToRem(12),
                }}
              >
                <TypographyWithI18N
                  id={`${groupId}-date`}
                  data-testid={`${groupId}-date`}
                  className='kfic2-metadata'
                  fontWeight='700'
                  i18nKey={
                    recentDaysLimit
                      ? getDateTextSuffixWithLimit(date, index, formattedDate)
                      : getDateTextSuffix(date, formattedDate)
                  }
                  textSuffix={enableDateTextSuffix ? dateTextSuffix : ''}
                />
                {noActivity ? (
                  <RecentHiringActivityChip
                    id={`${groupId}-no-activity`}
                    i18nKey='noHiringActivity'
                    role='listitem'
                    chipVariant='secondary'
                  />
                ) : (
                  activities.map((item, i) => {
                    return (
                      <RecentHiringActivityCard
                        key={i}
                        data={item}
                        id={`${groupId}-activity-card-${i}`}
                      />
                    );
                  })
                )}
              </Stack>
            );
          })}
          {endOfListI18Key && (
            <TypographyWithI18N
              i18nKey={endOfListI18Key}
              className='kfic2-metadata'
              color={`${colors.tertiary.darkGray}!important`}
              py={({ typography }: any) => typography.pxToRem(5)}
              textAlign='center'
            />
          )}
        </>
      ) : (
        <TypographyWithI18N
          id={`${id}-no-activity`}
          data-testid={`${id}-no-activity`}
          i18nKey={i18nKeyForEmptyState ?? 'noRecentHiringActivity'}
          className='kfic2-bodyText'
          role='listitem'
          sx={{
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        />
      )}
    </Stack>
  );
};

export default RecentHiringActivityGroupedList;
